import React, { useEffect, useState } from "react"
import { ChevronRightIcon } from "@heroicons/react/24/solid"
import { twMerge } from "tailwind-merge"
import ProductBox from "./ProductBox"
import Button from "aldoo-ra/Button"
import ScrollableRow from "./ScrollableRow"
import useT from "../hooks/useT"
import { CartPlacement } from "../store/placements"
import useAsync from "../hooks/useAsync"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { useCategories } from "../hooks/useCatalogData"
import { ListProducts, LIST_FIELDS } from "../api"
import { findCategory } from "../utils/categories"
import { isPhysicalProduct } from "../utils/productUtils"

// a request the API turned away is asked again after its lockout (10 s)
const RETRY_MS = 10 * 1000
const MAX_RETRIES = 3

/**
 * Titled horizontal row of products for a category ("Latest", "Popular", …).
 * `products` skips the fetch (homepage data), otherwise `book {list}` is called;
 * `initial` is that answer as the server had it (the home page's first rows):
 * shown at once, and replaced when the browser's own fetch answers.
 * `sortBy="weight"`: the category's own order (each product's weight in it,
 * set in Admin → Store → Products), the weighted products first.
 * `categories`: the ids to list from (a category with its whole subtree),
 * `category` still naming the row and its "See more"; `onSettled` is called
 * once the row has its answer, shown or empty (rows loaded one after another).
 */
export default function ProductScrollableList({
  category,
  categories: categoryIds,
  title,
  products,
  sortBy,
  sortDirection = -1,
  itemsPerPage = 20,
  moreProductsBtn = true,
  showTitle = true,
  filterPhysicalProducts = false,
  variant = "default",
  className = "",
  initial = null,
  // where an add from its cards is counted from (store/placements)
  placement = null,
  seeMoreHref,
  onSettled,
}) {
  const t = useT()
  const language = useSiteLanguage()
  const { list: categories } = useCategories()
  // The old site titled rows with the category name from the API (localized server-side)
  const label = findCategory(categories, category)?.name ?? title ?? ""

  const idsKey = categoryIds?.join(",") || ""
  const { result, loaded, refresh } = useAsync(
    () =>
      ListProducts({
        page: 0,
        ...(categoryIds?.length > 1 ? { categories: categoryIds } : { category: categoryIds?.[0] || category }),
        sortBy,
        sortDirection,
        items_per_page: itemsPerPage,
        language,
        owned_only: false,
        fields: LIST_FIELDS,
      }),
    [category, idsKey, sortBy, sortDirection, itemsPerPage, language],
    { enabled: !products, initial }
  )

  // the API turned the request away (too many from this visitor at once): a
  // row that waits on it (`onSettled`) asks again a little later — a few
  // times — and holds the rows after it until then
  const [retries, setRetries] = useState(0)
  const turnedAway = result?.error === "rate_limited"
  const retrying = !!onSettled && turnedAway && retries < MAX_RETRIES
  useEffect(() => {
    if (!retrying) return
    const timer = setTimeout(() => { setRetries((n) => n + 1); refresh() }, RETRY_MS)
    return () => clearTimeout(timer)
  }, [retrying, result, refresh])

  const settled = !!products || (loaded && !retrying)
  useEffect(() => {
    if (settled) onSettled?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled])

  let list = products ?? result?.list ?? []
  if (filterPhysicalProducts) list = list.filter((b) => !isPhysicalProduct(b))
  if ((products || result) && list.length === 0) return null

  return (
    <CartPlacement value={placement}>
    <section className={twMerge("w-full flex flex-col gap-2", className)}>
      <div className="w-full flex flex-row items-center justify-between px-4 sm:px-0">
        {showTitle && <h4 className="text-[20px] md:text-[22px] font-semibold text-text m-0">{label}</h4>}
        {moreProductsBtn && category && (
          <Button 
            variant="link" 
            href={seeMoreHref || `/category/${category}`} 
            className="inline-flex items-center gap-0.5 font-semibold"
          >
            {t`See more`}
            <ChevronRightIcon className="w-4 h-4 mt-[1px]" />
          </Button>
        )}
      </div>
      <ScrollableRow variant={variant}>
        {list.map((product) => (
          <ProductBox key={product.pid} product={product} variant={variant === "dark" ? "square_sm" : "default"} />
        ))}
        <div className="sm:hidden min-w-[16px] shrink-0" />
      </ScrollableRow>
    </section>
    </CartPlacement>
  )
}
